import { useMutation } from '@tanstack/react-query'
import { useChangeReceiptCustomer } from '@/api/receipts'
import { useCartStore } from '@/stores/cartStore'
import { usePOSStore, isReceiptPartiallyPaid } from '@/stores/posStore'
import { useUIStore } from '@/stores/uiStore'
import { V2ApiError } from '@/api/v2'
import type { Customer } from '@/types'

function extractTaxes(totals: Record<string, unknown>): number {
  const direct = Number(totals.taxes ?? totals.tax ?? 0)
  if (Number.isFinite(direct) && direct !== 0) return direct
  return (
    Number(totals.tax1_amount ?? totals.tax1 ?? 0)
    + Number(totals.tax2_amount ?? totals.tax2 ?? 0)
    + Number(totals.tax3_amount ?? totals.tax3 ?? 0)
    + Number(totals.tax4_amount ?? totals.tax4 ?? 0)
  )
}

/**
 * Apply a customer to the active receipt.
 *
 * - No backend receipt yet: optimistic local `setCustomer` only. The sync
 *   hook will create the receipt with the customer attached on the next
 *   item add (the POST body forwards `customer_id`).
 * - Backend receipt exists: optimistic local `setCustomer`, then call
 *   `PUT /receipts/{id}/customer` to drive the server-side reprice. The
 *   response carries the full re-priced receipt; we back-fill the cart's
 *   per-line prices and totals from it.
 *
 * On backend failure the local customer change is reverted so the cart
 * doesn't drift from the receipt.
 *
 * Use this instead of calling `usePOSStore().setCustomer` directly when
 * the cashier is choosing or clearing a customer in the POS UI.
 */
export function useSelectCustomer() {
  const changeReceiptCustomer = useChangeReceiptCustomer()

  return useMutation({
    mutationFn: async (customer: Customer | null): Promise<{ customer: Customer | null }> => {
      const {
        selectedCustomer: previousCustomer,
        currentReceiptId,
        setCustomer,
        setServerTotals,
        applyServerItemPrices,
        serverTotals,
        isRefundMode,
      } = usePOSStore.getState()

      // Block customer changes on a partially-paid receipt. The BE would
      // reject the PUT /receipts/{id}/customer with `cannot_cancel_paid`
      // (the receipt has live payment JTs), but doing the optimistic
      // local switch first leaves the cart momentarily showing the new
      // customer + tier prices that won't ever stick. Worse, a customer
      // swap re-prices line items, which means the cashier could be
      // staring at totals that don't match what was charged. Refund mode
      // is excluded (its receipt lifecycle is different and gated
      // elsewhere). Surface a toast so the cashier knows why the click
      // didn't take. posStore.setCustomer also no-ops here as the
      // data-safety backstop for the AgeVerifyModal direct call.
      //
      // Throw rather than resolve to previousCustomer so React Query's
      // onError fires for consumers (CustomerModal close-on-success,
      // CommandPalette clear-input-on-success, etc). Resolving silently
      // would let those consumers behave as if the change succeeded.
      // The thrown V2ApiError mimics the BE's own cannot_cancel_paid
      // shape so existing error-handler code paths (the messages dict
      // at L201-207 below) reuse cleanly if any caller wires up its
      // own onError mapping.
      if (isReceiptPartiallyPaid(serverTotals, isRefundMode)) {
        useUIStore.getState().showToast({
          type: 'warning',
          title: 'Receipt has payment(s) applied',
          message: 'Void the payment in Checkout before changing the customer.',
        })
        throw new V2ApiError(
          409,
          'Receipt has payment(s) applied — cannot change customer',
          'partial_payment_locked',
        )
      }

      // Drop the cashier's manually-typed discounts when the customer change
      // crosses a group boundary. Both receipt-level (cart.discount) and
      // per-line (item.discount_amount / item.discount_percent) get cleared.
      // Coupons gate the entire clear: if a coupon is active on the cart,
      // both the receipt-level discount and any per-line discounts survive
      // the customer change (per-line discounts don't carry their own "from
      // a coupon" flag, so we use the cart-level coupon flag as the proxy).
      // Per-line manual_price overrides are preserved per spec.
      //
      // The BE runs the canonical version of this on every PUT /receipts/
      // {id}/customer (clear_manual_discounts) and substitutes the
      // configured retail customer when no customer is attached. /cart/
      // preview is stateless though — the BE replays whatever the FE sends
      // — so without this optimistic FE clear the cashier sees the prior
      // discount stack on top of the new tier price.
      //
      // The FE state can hold null on `selectedCustomer` (we don't seed it
      // with the retail customer at session start), so the comparison
      // coerces null on both sides. Aligns with BE behavior in every common
      // flow except the explicit-pick-of-retail-on-empty-cart edge case.
      const oldGroupId = previousCustomer?.group_id ?? null
      const newGroupId = customer?.group_id ?? null
      if (oldGroupId !== newGroupId) {
        const cartState = useCartStore.getState()
        const couponActive = !!cartState.discount?.coupon
        if (!couponActive) {
          if (cartState.discount) {
            cartState.setDiscount(null)
          }

          // Per-line clear writes to both stores. ProductModal sets
          // discounts via posStore.updateCartItem; DiscountModal in
          // line-scope writes directly to cartStore.lines (and is the
          // only path for the discount_reason_* fields). The bridge
          // mirrors posStore → cartStore on customer change, so the
          // posStore clear suffices for ProductModal-set discounts; the
          // cartStore clear handles DiscountModal-set ones plus reason
          // metadata that only lives on cartStore.lines.
          const posState = usePOSStore.getState()
          let posCartChanged = false
          const cleanedPosCart = posState.cart.map((item) => {
            if (!item.discount_amount && !item.discount_percent) return item
            posCartChanged = true
            return {
              ...item,
              discount_amount: 0,
              discount_percent: 0,
              line_total: item.unit_price * item.quantity,
            }
          })
          if (posCartChanged) {
            usePOSStore.setState({ cart: cleanedPosCart })
          }

          let cartLinesChanged = false
          const cleanedLines = cartState.lines.map((line) => {
            if (
              line.discount_amount == null
              && line.discount_reason_id == null
              && line.discount_reason_other == null
            ) {
              return line
            }
            cartLinesChanged = true
            return {
              ...line,
              discount_amount: null,
              discount_reason_id: null,
              discount_reason_other: null,
            }
          })
          if (cartLinesChanged) {
            useCartStore.setState({ lines: cleanedLines, previewStatus: 'stale' })
          }
        }
      }

      // Optimistic local switch -- drives the React Query refetch of
      // /products/search with the new customer_id (Track 1.1) and lets the
      // CartSummary reflect the new customer-name immediately. The "syncing"
      // indicator is now driven by cartStore.previewStatus via the v2 preview
      // bridge; cartStore.setCustomer below flips lines to 'pending' which
      // the UI reads to show the spinner.
      setCustomer(customer)

      // No backend receipt yet -> nothing to reprice. Receipt creation in
      // useSyncReceiptTotals already includes customer_id when present.
      if (!currentReceiptId) {
        return { customer }
      }

      try {
        const rawReceipt = await changeReceiptCustomer.mutateAsync({
          receiptId: currentReceiptId,
          customerId: customer?.id ?? null,
        })

        // PUT /customer's response sometimes comes wrapped under
        // `{receipt: ...}` (same envelope shape v2's GET /receipts/{id}
        // uses — see useSyncReceiptTotals.ts:407 for the same defensive
        // unwrap). Without this, `receipt.items` was `undefined` →
        // `applyServerItemPrices([])` no-opped → cart lines stayed at
        // their pre-customer shelf price even though serverTotals (read
        // from a wrapped totals object via the `??` fallback below) and
        // the receipt itself carried the correct tier price. Symptom:
        // cart line displayed $19.03 while subtotal/total showed the
        // tier-correct $15.43.
        const maybeWrapped = rawReceipt as unknown as { receipt?: unknown }
        const receipt =
          maybeWrapped?.receipt && typeof maybeWrapped.receipt === 'object'
            ? maybeWrapped.receipt
            : rawReceipt

        // Apply server-side reprice to the cart. The receipt items carry
        // the new tier prices and any re-evaluated scheduled discounts.
        const items = Array.isArray((receipt as { items?: unknown }).items)
          ? ((receipt as { items: unknown[] }).items as Parameters<typeof applyServerItemPrices>[0])
          : []
        if (items.length > 0) {
          applyServerItemPrices(items)
        }

        const totals = ((receipt as unknown as { totals?: Record<string, unknown> }).totals ?? {}) as Record<string, unknown>
        setServerTotals({
          subtotal: Number(totals.subtotal ?? 0),
          taxes: extractTaxes(totals),
          deposit: Number(totals.deposit_amount ?? totals.recycling_amount ?? 0),
          total: Number(totals.total ?? 0),
          amountToPay: Number(
            (receipt as { amount_to_pay?: number | string }).amount_to_pay ?? totals.total ?? 0,
          ),
        })

        return { customer }
      } catch (err) {
        // Revert local customer so the cart doesn't disagree with the
        // backend receipt's customer_id.
        setCustomer(previousCustomer)

        // Surface a toast so the cashier knows the change didn't stick.
        // Without this, a 409 (e.g. backend's TAX_MISMATCH bug — see
        // V2-BACKEND-FOLLOWUPS.md item 6) silently reverts and the
        // cashier wonders why nothing happened. Specific error codes
        // get tailored copy; everything else falls through to the
        // generic message.
        const apiErr = err as {
          code?: string
          message?: string
          response?: { data?: { code?: string; error?: string } }
        }
        const code = apiErr?.code ?? apiErr?.response?.data?.code
        const detail = apiErr?.message ?? apiErr?.response?.data?.error
        const messages: Record<string, string> = {
          cannot_cancel_paid: 'This receipt is already paid — change customer before payment.',
          payment_in_progress: 'A payment is in progress. Try again in a moment.',
          already_terminal: 'This receipt is closed and can\'t be changed.',
          operation_failed: 'Receipt is locked. Reload to continue.',
          receipt_sanity_failed: 'Receipt is out of sync. Reload to continue.',
        }
        useUIStore.getState().showToast({
          type: 'error',
          title: customer ? 'Could Not Apply Customer' : 'Could Not Clear Customer',
          message: (code && messages[code]) || detail || 'Please try again.',
        })

        throw err
      }
    },
  })
}
